import { useState, useEffect } from "react";
import {
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Lock, Eye, Download, Users, ArrowLeft, Loader2, FileCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import FsLogo from "@/components/FsLogo";

const ADMIN_PASSPHRASE = "fabio30";
const BUCKET = "documents";

export default function AdminPanel() {
  const [passphrase, setPassphrase] = useState("");
  const [authenticated, setAuthenticated] = useState(false);
  const [rsvps, setRsvps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [docDialog, setDocDialog] = useState(null);
  const [docUrl, setDocUrl] = useState(null);
  const [docLoading, setDocLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!passphrase.trim()) return;
    if (passphrase.trim() !== ADMIN_PASSPHRASE) {
      toast.error("Passphrase non valida.");
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("rsvps")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      setRsvps(data || []);
      setAuthenticated(true);
    } catch {
      toast.error("Errore nel caricamento dei dati.");
    } finally {
      setLoading(false);
    }
  };

  const viewDocument = async (rsvp) => {
    setDocDialog(rsvp);
    setDocLoading(true);
    setDocUrl(null);
    try {
      const { data, error } = await supabase.storage
        .from(BUCKET)
        .createSignedUrl(rsvp.storage_path, 60);
      if (error) throw error;
      setDocUrl(data.signedUrl);
    } catch {
      toast.error("Errore nel caricamento del documento.");
    } finally {
      setDocLoading(false);
    }
  };

  const downloadDocument = async (rsvp) => {
    try {
      const { data, error } = await supabase.storage
        .from(BUCKET)
        .download(rsvp.storage_path);
      if (error) throw error;
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = rsvp.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Errore nel download.");
    }
  };

  const deleteRsvp = async (rsvp) => {
    if (!window.confirm(`Eliminare l'arruolamento di ${rsvp.nome} ${rsvp.cognome}?`)) return;
    try {
      await supabase.storage.from(BUCKET).remove([rsvp.storage_path]);
      const { error } = await supabase.from("rsvps").delete().eq("id", rsvp.id);
      if (error) throw error;
      setRsvps((prev) => prev.filter((r) => r.id !== rsvp.id));
      toast.success("Arruolamento eliminato.");
    } catch {
      toast.error("Errore nell'eliminazione.");
    }
  };

  useEffect(() => {
    return () => {
      if (docUrl) URL.revokeObjectURL(docUrl);
    };
  }, [docUrl]);

  /* ===================== LOGIN ===================== */
  if (!authenticated) {
    return (
      <div className="relative min-h-[100dvh] flex items-center justify-center px-6 bg-ink grid-bg overflow-hidden">
        <div className="relative z-10 w-full max-w-sm">
          <div className="flex flex-col items-center gap-6 mb-10">
            <FsLogo size={64} />
            <div className="rule-dotted w-full max-w-[160px]" />
            <div className="text-center">
              <h1 className="font-display text-bone tracking-wide text-3xl mb-2">MISSION CONTROL</h1>
              <p className="font-mono italic text-bone-dim text-sm">Solo per gli occhi di Fabio.</p>
            </div>
          </div>

          <form onSubmit={handleLogin} className="bg-ink-2 border border-line p-8">
            <label htmlFor="passphrase" className="field-label flex items-center gap-2 mb-3">
              <Lock className="w-3.5 h-3.5" strokeWidth={1.8} />
              PASSPHRASE
            </label>
            <input
              id="passphrase"
              type="password"
              data-testid="admin-passphrase-input"
              value={passphrase}
              onChange={(e) => setPassphrase(e.target.value)}
              className="field-input mb-8"
              autoComplete="current-password"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-btn"
              className="btn btn-primary w-full py-3.5"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Accedi"}
            </button>
          </form>

          <button
            onClick={() => navigate("/")}
            className="mt-8 flex items-center gap-2 text-sm text-bone-dim hover:text-red transition-colors mx-auto font-mono"
            data-testid="admin-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Torna alla home
          </button>
        </div>
      </div>
    );
  }

  /* ===================== DASHBOARD ===================== */
  const docsUploaded = rsvps.filter((r) => r.original_filename).length;

  return (
    <div className="min-h-[100dvh] px-6 py-12 bg-ink">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-4">
            <FsLogo size={44} />
            <div>
              <h1 className="font-display text-bone tracking-wide text-2xl sm:text-3xl">
                ROSTER OPERATIVO
              </h1>
              <p className="font-mono text-bone-dim text-xs sm:text-sm mt-1.5">
                Operazione Trenta / personale arruolato
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate("/")}
            className="text-sm text-bone-dim hover:text-red transition-colors flex items-center gap-2 font-mono"
            data-testid="admin-home-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Home
          </button>
        </div>

        <div className="rule mb-10" />

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-line border border-line mb-10">
          <div className="bg-ink-2 p-6 flex items-center gap-4">
            <div className="w-12 h-12 border border-[rgba(201,164,122,0.4)] flex items-center justify-center shrink-0">
              <Users className="w-5 h-5 text-red" strokeWidth={1.5} />
            </div>
            <div>
              <p
                className="font-mono font-bold text-bone text-3xl tabular-nums leading-none"
                data-testid="admin-rsvp-count"
              >
                {String(rsvps.length).padStart(2, "0")}
              </p>
              <p className="field-label mt-2.5">AGENTI A BORDO</p>
            </div>
          </div>
          <div className="bg-ink-2 p-6 flex items-center gap-4">
            <div className="w-12 h-12 border border-[rgba(201,164,122,0.4)] flex items-center justify-center shrink-0">
              <FileCheck className="w-5 h-5 text-red" strokeWidth={1.5} />
            </div>
            <div>
              <p
                className="font-mono font-bold text-bone text-3xl tabular-nums leading-none"
                data-testid="admin-docs-count"
              >
                {String(docsUploaded).padStart(2, "0")}
              </p>
              <p className="field-label mt-2.5">DOCUMENTI VERIFICATI</p>
            </div>
          </div>
        </div>

        {/* Table or empty */}
        {rsvps.length === 0 ? (
          <div className="bg-ink-2 border border-line text-center py-20" data-testid="admin-no-rsvps">
            <Users className="w-10 h-10 text-bone-faint mx-auto mb-4" strokeWidth={1.5} />
            <p className="font-mono italic text-bone-dim text-sm">
              Nessun agente ancora a bordo. Aspettiamo i ritardatari.
            </p>
          </div>
        ) : (
          <div className="bg-ink-2 border border-line overflow-x-auto" data-testid="admin-rsvp-table">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-line hover:bg-transparent">
                  <TableHead className="field-label h-12">Nome</TableHead>
                  <TableHead className="field-label h-12">Cognome</TableHead>
                  <TableHead className="field-label h-12">Finestra</TableHead>
                  <TableHead className="field-label h-12">Documento</TableHead>
                  <TableHead className="field-label h-12">Arruolato il</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rsvps.map((r) => (
                  <TableRow
                    key={r.id}
                    className="border-b border-line hover:bg-[rgba(201,164,122,0.07)]"
                    data-testid={`admin-rsvp-row-${r.id}`}
                  >
                    <TableCell className="font-grotesk text-bone text-base">{r.nome}</TableCell>
                    <TableCell className="font-grotesk text-bone text-base">{r.cognome}</TableCell>
                    <TableCell className="font-mono text-red text-xs tracking-wider">
                      {r.date_choice}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => viewDocument(r)}
                          className="p-2 text-bone-dim hover:text-red transition-colors"
                          data-testid={`view-doc-${r.id}`}
                          title="Visualizza"
                        >
                          <Eye className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                        <button
                          onClick={() => downloadDocument(r)}
                          className="p-2 text-bone-dim hover:text-red transition-colors"
                          data-testid={`download-doc-${r.id}`}
                          title="Scarica"
                        >
                          <Download className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                        <button
                          onClick={() => deleteRsvp(r)}
                          className="p-2 text-bone-dim hover:text-red transition-colors"
                          data-testid={`delete-rsvp-${r.id}`}
                          title="Elimina"
                        >
                          <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-bone-faint text-xs">
                      {new Date(r.created_at).toLocaleDateString("it-IT", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <Dialog
        open={!!docDialog}
        onOpenChange={() => {
          setDocDialog(null);
          setDocUrl(null);
        }}
      >
        <DialogContent className="max-w-2xl bg-ink-2 border border-line rounded-none text-bone">
          <DialogHeader>
            <DialogTitle className="font-display text-bone tracking-wide text-xl">
              {docDialog?.nome?.toUpperCase()} {docDialog?.cognome?.toUpperCase()}
            </DialogTitle>
            <DialogDescription className="font-mono text-bone-dim text-sm">
              {docDialog?.original_filename}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 min-h-[300px] flex items-center justify-center">
            {docLoading ? (
              <Loader2 className="w-8 h-8 text-red animate-spin" />
            ) : docUrl ? (
              docDialog?.content_type === "application/pdf" ? (
                <iframe src={docUrl} className="w-full h-[500px] border border-line" title="Document" />
              ) : (
                <img src={docUrl} alt="Documento" className="max-w-full max-h-[500px] object-contain" />
              )
            ) : (
              <p className="font-mono italic text-bone-dim">Errore nel caricamento</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
